import { CancelTokenSource } from 'axios';

import { AuditRecord, SearchResult, UserRef } from '@rygen/rygen-ui/model';
import { Order as EsOrder } from '@rygen/tms-search-model/search/order';

import {
  CancelOrderCommand,
  CloneOrderCommand,
  ES_ORDER_COLUMNS_SORT_FIELDS,
  HttpHeader,
  Order,
  ORDER_COLUMNS_SORT_FIELDS,
  OrderSearchCriteria,
  OrderSearchResult,
  RateShop,
  SearchOptions,
  Shipment,
  ShipmentSpec,
  ShipmentTransportSegment,
  TenderAuthorizationDecision,
} from '@/model';
import { ValidShipOrder } from '@/model/routeBuilder';

import { BaseHttpController } from './BaseHttpController';

const endpoint = 'order';

const DEFAULT_SEARCH_OPTIONS: SearchOptions = {
  useCache: true,
  useSelectedDomainContext: true,
};

const FIND_RELATED_SHIP_ORDERS_DEFAULT_OPTIONS = {
  onlyUnplanned: false,
};

export class OrderController extends BaseHttpController {
  constructor() {
    super(endpoint);
  }

  async create(order: Order): Promise<Order> {
    if (order.id != null) {
      throw new Error('Id expected to be null when calling create');
    }

    const response = await this.http.post<Order>(
      '',
      { order },
      {
        headers: {
          [HttpHeader.SOURCE]: 'Order Entry',
        },
      },
    );
    return new Order(response.data);
  }

  async findById(orderId: number): Promise<Order> {
    const response = await this.http.get<Order>(`${orderId}`);
    return new Order(response.data);
  }

  async search(
    criteria: OrderSearchCriteria,
    opts: SearchOptions = DEFAULT_SEARCH_OPTIONS,
  ): Promise<OrderSearchResult> {
    const options: SearchOptions = { ...DEFAULT_SEARCH_OPTIONS, ...opts };
    criteria.referenceSort =
      criteria.sortField !== undefined &&
      !ORDER_COLUMNS_SORT_FIELDS.includes(criteria.sortField);
    const response = await this.http.post<OrderSearchResult>(
      'search',
      criteria,
      {
        cancelToken: options.cancelToken?.token,
        params: {
          useCache: !!options.useCache,
          useSelectedDomainContext: !!options.useSelectedDomainContext,
        },
      },
    );
    return new OrderSearchResult(response.data);
  }

  async searchWithElasticSearch(
    criteria: OrderSearchCriteria,
    opts: SearchOptions = DEFAULT_SEARCH_OPTIONS,
  ): Promise<SearchResult<EsOrder>> {
    const options: SearchOptions = { ...DEFAULT_SEARCH_OPTIONS, ...opts };
    criteria.referenceSort =
      criteria.sortField !== undefined &&
      !ES_ORDER_COLUMNS_SORT_FIELDS.includes(criteria.sortField);
    const response = await this.http.post<SearchResult<EsOrder>>(
      'query',
      criteria,
      {
        cancelToken: options.cancelToken?.token,
        params: {
          useCache: !!options.useCache,
          useSelectedDomainContext: !!options.useSelectedDomainContext,
        },
      },
    );
    return new SearchResult(response.data);
  }

  async update(order: Order): Promise<Order> {
    if (order.id == null) {
      throw new Error('Id is required to perform update');
    }

    const response = await this.http.put<Order>('', { order });
    return new Order(response.data);
  }

  async updateTags(
    order: Order,
    tags: string[],
    cancelToken?: CancelTokenSource,
  ): Promise<Order> {
    if (order.id == null) {
      throw new Error('Id is required to perform update');
    }

    const response = await this.http.put<Order>(
      'tags',
      { orderId: order.id, tags },
      { cancelToken: cancelToken?.token },
    );

    return new Order(response.data);
  }

  async updateAssignedUsers(
    orderId: number,
    assignedUsers: UserRef[],
  ): Promise<Order> {
    const response = await this.http.put<Order>('users', {
      orderId: orderId,
      users: assignedUsers,
    });

    return new Order(response.data);
  }

  async updateCarrierNotes(
    orderId: number,
    carrierNotes: string,
  ): Promise<Order> {
    const response = await this.http.put<Order>('carrier-notes', {
      orderId,
      carrierNotes,
    });

    return new Order(response.data);
  }

  async cancel(
    orderId: number,
    cancelOrderCommand: CancelOrderCommand,
  ): Promise<Order> {
    const response = await this.http.post<Order>(
      `${orderId}/cancel`,
      cancelOrderCommand,
    );
    return new Order(response.data);
  }

  async clone(
    orderId: number,
    cloneOrderCommand: CloneOrderCommand,
  ): Promise<Order> {
    const response = await this.http.post<Order>(
      `${orderId}/clone`,
      cloneOrderCommand,
      {
        headers: {
          [HttpHeader.SOURCE]: 'Order Detail',
        },
      },
    );

    return new Order(response.data);
  }

  async updateRates(orderId: number): Promise<RateShop> {
    const response = await this.http.post<RateShop>(`${orderId}/rate`);
    return new RateShop(response.data);
  }

  async findAllRelatedShipOrders(
    orderId: number,
    options = FIND_RELATED_SHIP_ORDERS_DEFAULT_OPTIONS,
  ): Promise<ValidShipOrder[]> {
    const params = {
      ...FIND_RELATED_SHIP_ORDERS_DEFAULT_OPTIONS,
      ...options,
    };

    const response = await this.http.get<ValidShipOrder[]>(
      `${orderId}/ship-order`,
      { params },
    );
    return response.data.map((val) => new ValidShipOrder(val));
  }

  async findAllRelatedShipmentTransportSegments(
    orderId: number,
  ): Promise<ShipmentTransportSegment[]> {
    const response = await this.http.get<ShipmentTransportSegment[]>(
      `${orderId}/transport-segment`,
    );
    return response.data.map((val) => new ShipmentTransportSegment(val));
  }

  async findAllRelatedShipments(orderId: number): Promise<Shipment[]> {
    const response = await this.http.get<Shipment[]>(`${orderId}/shipment`);
    return response.data.map((val) => new Shipment(val));
  }

  async findAllRelatedShipmentSpecs(orderId: number): Promise<ShipmentSpec[]> {
    const response = await this.http.get<ShipmentSpec[]>(
      `${orderId}/shipment-spec`,
    );
    return response.data.map((val) => new ShipmentSpec(val));
  }

  async findOrderAudits(orderId: number): Promise<AuditRecord[]> {
    const response = await this.http.get<AuditRecord[]>(`${orderId}/audit`);
    return response.data.map((val) => new AuditRecord(val));
  }

  async authorizeTender(
    orderId: number,
    rateQuoteId: number,
  ): Promise<TenderAuthorizationDecision> {
    const response = await this.http.get<TenderAuthorizationDecision>(
      `${orderId}/tender-authorization/${rateQuoteId}`,
    );
    return response.data;
  }
}
